import { LegacyRef, useRef } from 'react';
import {
  FieldPath,
  FieldValues,
  UseControllerProps,
  useController,
} from 'react-hook-form';
import NewFile from '../../../assets/img/file-new.svg';
import { MAX_FILE_SIZE } from '../../../constants/FileConstants';
import { showErrorToast } from '../../../slices/ToastNotificationSlice';
import { useAppDispatch } from '../../../slices/store';
import {
  bytesToSize,
  isDocSizeInvalid,
  validDocs,
} from '../../../utils/FileUtils';
import DefaultLoader from '../../DefaultLoader';
import FieldErrorMessage from '../FieldErrorMessage';

export interface ControlledSingleFileInputV2Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> extends UseControllerProps<TFieldValues, TName> {
  onChangeSpy?: (value: File) => void;
  isUploading?: boolean;
  maxUploadSize?: number;
}

const ControlledSingleFileInputV2 = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  control,
  name,
  onChangeSpy,
  isUploading,
  maxUploadSize = MAX_FILE_SIZE,
  ...props
}: ControlledSingleFileInputV2Props<TFieldValues, TName>) => {
  const dispatch = useAppDispatch();
  const fileInputRef = useRef<HTMLDivElement>(null);
  const {
    field: { onChange },
    fieldState,
  } = useController({
    name,
    control,
  });

  return (
    <div>
      <div
        className='flex flex-col bg-reskin-regent-200 p-[18px] md:px-20 md:py-6 border border-dashed rounded-lg border-reskin-grey-50 hover:border-[#9CC2FF] items-center justify-center cursor-pointer'
        onClick={() => {
          if (!isUploading) {
            fileInputRef.current?.click();
          }
        }}
        onDragOver={(e) => e.preventDefault()}
        onDragEnter={(e) => e.preventDefault()}
        onDragLeave={(e) => e.preventDefault()}
        onDrop={async (e) => {
          if (!isUploading) {
            e.preventDefault();
            const files = Array.from(e.dataTransfer.files);
            const validFiles = validDocs(files, maxUploadSize);

            if (validFiles?.length) {
              onChange(validFiles?.[0]);
              if (onChangeSpy) {
                onChangeSpy(validFiles?.[0]);
              }
            }

            if (isDocSizeInvalid(files?.[0], maxUploadSize)) {
              dispatch(
                showErrorToast(
                  `File size exceeds maximum limit of ${bytesToSize(
                    maxUploadSize,
                  )}.`,
                ),
              );
            }
          }
        }}
      >
        <input
          {...props}
          id={name}
          data-testId={name}
          ref={fileInputRef as unknown as LegacyRef<HTMLInputElement>}
          className='hidden w-full h-full'
          type='file'
          accept='.xlsx, .xls, .doc, .docx, .pdf, image/*'
          onChange={(e) => {
            const files = Array.from(e.target.files!);
            const validFiles = validDocs(files, maxUploadSize);

            if (validFiles?.length) {
              onChange(validFiles?.[0]);

              if (onChangeSpy) {
                onChangeSpy(validFiles?.[0]);
              }
            }

            if (isDocSizeInvalid(files?.[0], maxUploadSize)) {
              dispatch(
                showErrorToast(
                  `File size exceeds maximum limit of ${bytesToSize(
                    maxUploadSize,
                  )}.`,
                ),
              );
            }
          }}
          onClick={(e) => {
            // This is to reset the file input value when the user tries to upload the same file again
            e.currentTarget.value = '';
          }}
          multiple={false}
          disabled={isUploading}
        />
        {isUploading ? (
          <div>
            <DefaultLoader />
          </div>
        ) : (
          <div className='flex flex-col items-center gap-y-2'>
            <div className='flex items-center justify-center gap-x-2'>
              <img src={NewFile} alt='new-file' width={20} height={20} />
              <p className='flex items-center gap-x-2'>
                <span className='font-inter font-medium text-sm text-reskin-primary-blue cursor-pointer'>
                  Browse Files
                </span>
                <p className='font-inter font-normal text-sm text-reskin-regent-600 text-center'>
                  or Drag & Drop
                </p>
              </p>
            </div>
            <p className='font-inter font-normal text-sm text-center text-reskin-grey-500'>
              Please upload only .pdf, .doc or .docx files
            </p>
          </div>
        )}
      </div>
      <FieldErrorMessage message={fieldState.error?.message} />
    </div>
  );
};

export default ControlledSingleFileInputV2;
